import { Box, Chip, MenuItem, Select, SelectChangeEvent, Slider, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import {
  CheckedIcon,
  OverviewIcon,
  PlayingLevelIcon,
  ProfessionalInfoIcon,
  SkillsIcon,
  UnCheckedIcon,
} from "../../../assets/icons/MUI/AdvancedSearch";
import { colors } from "../../../theme/palette";
import SectionHeader from "./Components/SectionHeader";
import FilterBox from "./Components/FilterBox";
import { getAdvancedSearchLists, getSkillsLists } from "../../../api/network";
import { PlayerFilters } from "../../../types";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

type PlayingLevels = {
  name: string;
  id: number;
};

type Skill = {
  id: number;
  lang: string;
  localizedId: number;
  name: string;
  role: string;
  status: string;
  subRole: string | null;
};

const PlayerFilter = ({
  advancedFilters,
  setAdvancedFilters,
  isFilterCleared,
}: {
  advancedFilters: PlayerFilters;
  setAdvancedFilters: React.Dispatch<React.SetStateAction<PlayerFilters>>;
  isFilterCleared: boolean;
}) => {
  const { t } = useTranslation();

  const positions = [
    { label: t("components.networkController.advancedFilterPopUp.PlayerPosition"), value: "" },
    { label: t("components.networkController.advancedFilterPopUp.GOALKEEPER (GK)"), value: "GOALKEEPER (GK)" },
    { label: t("components.networkController.advancedFilterPopUp.LEFT BACK (LB)"), value: "LEFT BACK (LB)" },
    { label: t("components.networkController.advancedFilterPopUp.CENTER BACK (CB)"), value: "CENTER BACK (CB)" },
    { label: t("components.networkController.advancedFilterPopUp.SWEEPER (SW)"), value: "SWEEPER (SW)" },
    { label: t("components.networkController.advancedFilterPopUp.RIGHT BACK (RB)"), value: "RIGHT BACK (RB)" },
    {
      label: t("components.networkController.advancedFilterPopUp.LEFT MIDFIELDER (LMF)"),
      value: "LEFT MIDFIELDER (LMF)",
    },
    {
      label: t("components.networkController.advancedFilterPopUp.CENTER MIDFIELDER (CMF)"),
      value: "CENTER MIDFIELDER (CMF)",
    },
    {
      label: t("components.networkController.advancedFilterPopUp.RIGHT MIDFIELDER (RMF)"),
      value: "RIGHT MIDFIELDER (RMF)",
    },
    {
      label: t("components.networkController.advancedFilterPopUp.ATTACKING MIDFIELDER (AMF)"),
      value: "ATTACKING MIDFIELDER (AMF)",
    },
    {
      label: t("components.networkController.advancedFilterPopUp.DEFENSIVE MIDFIELDER (DMF)"),
      value: "DEFENSIVE MIDFIELDER (DMF)",
    },
    { label: t("components.networkController.advancedFilterPopUp.LEFT WINGER (LW)"), value: "LEFT WINGER (LW)" },
    { label: t("components.networkController.advancedFilterPopUp.CENTER FORWARD (CF)"), value: "CENTER FORWARD (CF)" },
    { label: t("components.networkController.advancedFilterPopUp.RIGHT WINGER (RW)"), value: "RIGHT WINGER (RW)" },
    { label: t("components.networkController.advancedFilterPopUp.STRIKER (ST)"), value: "STRIKER (ST)" },
  ];

  const initialFilters = {
    primaryPosition: advancedFilters.primaryPosition || "",
    otherPosition: advancedFilters.otherPosition || "",
    ageRange: (advancedFilters.ageRange as [number, number]) || ([1, 100] as [number, number]),
    weightRange: (advancedFilters.weightRange as [number, number]) || ([1, 100] as [number, number]),
    heightRange: (advancedFilters.heightRange as [number, number]) || ([1, 250] as [number, number]),
    hasAgent: advancedFilters.hasAgent || false,
    hasFootballExperience: advancedFilters.hasFootballExperience || false,
    hasCertificateOrLicense: advancedFilters.hasFootballExperience || false,
    skills: advancedFilters.skills || ([] as number[]), // Change type to number[]
    playingLevels: advancedFilters.playingLevels || ([] as string[]), // Add playingLevels property
  };

  const { data: playingLevels } = useQuery<PlayingLevels[]>({
    queryKey: ["playingLevels"],
    queryFn: () => getAdvancedSearchLists("playingLevel"),
    initialData: () => [] as Skill[],
    staleTime: 0,
  });

  const { data: playerSkills } = useQuery<Skill[]>({
    queryKey: ["playerSkills"],
    queryFn: () => getSkillsLists("player"),
    initialData: [] as Skill[],
    staleTime: 0,
  });

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    setFilters(initialFilters);
  }, [isFilterCleared]);

  useEffect(() => {
    setAdvancedFilters(filters);
  }, [filters]);

  type FilterKeys = keyof typeof filters;

  const handleSliderChange = (name: FilterKeys) => (event: Event, newValue: number | number[]) => {
    setFilters((prevState) => ({ ...prevState, [name]: newValue }));
  };

  const handleSwitchChange = (name: FilterKeys) => () => {
    setFilters((prevState) => ({ ...prevState, [name]: !prevState[name] }));
  };

  const handleSkillClick = (skill: number) => () => {
    setFilters((prevState) => {
      const newSkills = prevState.skills.includes(skill)
        ? prevState.skills.filter((s) => s !== skill)
        : [...prevState.skills, skill];
      return { ...prevState, skills: newSkills };
    });
  };

  const handlePlayingLevelClick = (levelId: number) => () => {
    setFilters((prevState) => {
      const newLevels = prevState.playingLevels.includes(levelId.toString())
        ? prevState.playingLevels.filter((l) => l !== levelId.toString())
        : [...prevState.playingLevels, levelId.toString()];
      return { ...prevState, playingLevels: newLevels };
    });
  };

  return (
    <>
      <SectionHeader icon={OverviewIcon} title={t("components.networkController.advancedFilterPopUp.playerOverview")} />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.primaryPosition")}>
        <Select
          value={filters.primaryPosition}
          onChange={(event: SelectChangeEvent) =>
            setFilters((prevState) => ({
              ...prevState,
              primaryPosition: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {positions.map((position) => (
            <MenuItem key={position.value} value={position.value}>
              {position.label}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.otherPosition")}>
        <Select
          value={filters.otherPosition}
          onChange={(event: SelectChangeEvent) =>
            setFilters((prevState) => ({
              ...prevState,
              otherPosition: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {positions.map((position) => (
            <MenuItem key={position.value} value={position.value}>
              {position.label}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.ageRange")}>
        <Slider
          value={filters.ageRange}
          onChange={handleSliderChange("ageRange")}
          valueLabelDisplay="auto"
          getAriaValueText={(value: number) => `${value} ${t("components.networkController.advancedFilterPopUp.age")}`}
          valueLabelFormat={(value: number) => `${value} ${t("components.networkController.advancedFilterPopUp.age")}`}
          sx={{ maxWidth: "360px", width: "50%" }}
          min={1}
          max={100}
        />
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.weightRange")}>
        <Slider
          value={filters.weightRange}
          onChange={handleSliderChange("weightRange")}
          valueLabelDisplay="auto"
          getAriaValueText={(value: number) =>
            `${value} ${t("components.networkController.advancedFilterPopUp.weight")}`
          }
          valueLabelFormat={(value: number) =>
            `${value} ${t("components.networkController.advancedFilterPopUp.weight")}`
          }
          sx={{ maxWidth: "360px", width: "50%" }}
          min={1}
          max={100}
        />
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.heightRange")}>
        <Slider
          value={filters.heightRange}
          onChange={handleSliderChange("heightRange")}
          valueLabelDisplay="auto"
          getAriaValueText={(value: number) => `${value} ${t("components.networkController.advancedFilterPopUp.cm")}`}
          valueLabelFormat={(value: number) => `${value} ${t("components.networkController.advancedFilterPopUp.cm")}`}
          sx={{ maxWidth: "360px", width: "50%" }}
          min={1}
          max={250}
        />
      </FilterBox>
      <SectionHeader
        icon={ProfessionalInfoIcon}
        title={t("components.networkController.advancedFilterPopUp.professionalInfo")}
      />
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasAgent")}>
        <Switch checked={filters.hasAgent} onChange={handleSwitchChange("hasAgent")} />
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasFootballExperience")}>
        <Switch checked={filters.hasFootballExperience} onChange={handleSwitchChange("hasFootballExperience")} />
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.hasCertificateOrLicense")}>
        <Switch checked={filters.hasCertificateOrLicense} onChange={handleSwitchChange("hasCertificateOrLicense")} />
      </FilterBox>
      <SectionHeader icon={SkillsIcon} title={t("components.networkController.advancedFilterPopUp.skills")} />
      <Box
        sx={{
          paddingBottom: "30px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 12px",
        }}
      >
        {playerSkills.map((skill, index) => (
          <Chip
            key={index}
            color={filters.skills.includes(skill.id) ? "primary" : "secondary"}
            label={skill.name}
            icon={
              filters.skills.includes(skill.id) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handleSkillClick(skill.id)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.skills.includes(skill.id) ? {} : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>
      <SectionHeader
        icon={PlayingLevelIcon}
        title={t("components.networkController.advancedFilterPopUp.playingLevel")}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "8px",
          rowGap: "12px",
        }}
      >
        {playingLevels.map((level) => (
          <Chip
            key={level.id}
            color={filters.playingLevels.includes(level.id.toString()) ? "primary" : "secondary"}
            label={level.name}
            icon={
              filters.playingLevels.includes(level.id.toString()) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handlePlayingLevelClick(level.id)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.playingLevels.includes(level.id.toString())
                ? {}
                : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default PlayerFilter;
